import { X, Crown, Palette, Image as ImageIcon, Sparkles, Loader } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProductByInterval } from '../stripe-config';
import { supabase } from '../lib/supabase';

interface ProUpgradeModalProps {
  onClose: () => void;
}

export function ProUpgradeModal({ onClose }: ProUpgradeModalProps) {
  const [interval, setInterval] = useState<'month' | 'year'>('year');
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const { user } = useAuth();

  const handleSubscribe = async () => {
    if (!user) {
      alert('Please sign in to subscribe');
      return;
    }

    const product = getProductByInterval(interval);
    if (!product) {
      alert('Product configuration not found. Please contact support.');
      return;
    }

    if (!product.priceId || product.priceId.includes('default')) {
      alert('Stripe is not configured yet. Please add VITE_STRIPE_MONTHLY_PRICE_ID and VITE_STRIPE_YEARLY_PRICE_ID to your environment variables.');
      return;
    }

    setLoading(true);

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.data.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            price_id: product.priceId,
            mode: 'subscription',
            success_url: `${window.location.origin}/payment-success`,
            cancel_url: window.location.origin,
            promo_code: promoCode.trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Checkout API error:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error instanceof Error ? error.message : 'Failed to start checkout');
      setLoading(false);
    }
  };

  const monthlyPrice = 2.99;
  const yearlyPrice = 19.99;
  const monthlySavings = ((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12) * 100).toFixed(0);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          disabled={loading}
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            You're loving Countr!
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Upgrade to Pro for unlimited countdowns and exclusive features
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Unlimited Countdowns</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Create as many countdowns as you want</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Palette className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Custom Themes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Personalize with exclusive color schemes</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Background Images</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Add photos to your countdown cards</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-1 mb-6 flex">
          <button
            onClick={() => setInterval('month')}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
              interval === 'month'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-md'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="text-sm">Monthly</div>
            <div className="text-lg font-bold">${monthlyPrice}</div>
            <div className="text-xs opacity-70">/month</div>
          </button>
          <button
            onClick={() => setInterval('year')}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 relative ${
              interval === 'year'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-md'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="absolute -top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
              Save {monthlySavings}%
            </div>
            <div className="text-sm">Yearly</div>
            <div className="text-lg font-bold">${yearlyPrice}</div>
            <div className="text-xs opacity-70">/year</div>
          </button>
        </div>

        <div className="mb-4">
          <label htmlFor="promo-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Promo Code (Optional)
          </label>
          <input
            id="promo-code"
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            disabled={loading}
            placeholder="Enter promo code"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <button
          onClick={handleSubscribe}
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            'Subscribe Now'
          )}
        </button>
      </div>
    </div>
  );
}